import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import DashboardLayout from '../layout/DashboardLayout';
import { config } from '../../config/config';
import { showToast } from '../utils/Toast';
import ConfirmDialog from '../common/ConfirmDialog';
import PromptDialog from '../common/PromptDialog';

const ACTIVE_KEY = 'topsailAiChat:activeId:v3';

// ---------- Tool event chips ----------

const formatToolEvent = (ev) => {
  const a = ev.args || {};
  const bits = [];
  if (a.entity) bits.push(a.entity);
  if (a.search) bits.push(`"${a.search}"`);
  if (a.id) bits.push(`id=${String(a.id).slice(-6)}`);
  if (a.filter && Object.keys(a.filter).length) bits.push(JSON.stringify(a.filter));
  return `${ev.tool}(${bits.join(' ')})`;
};

// ---------- Markdown renderer ----------

const escapeHtml = (s) => String(s)
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;');

const FILE_EXTENSIONS = /\.(pdf|doc|docx|xls|xlsx|png|jpg|jpeg|gif|webp|svg|zip|csv)$/i;

const renderInline = (s) => escapeHtml(s)
  .replace(/`([^`]+)`/g, '<code class="px-1 py-0.5 bg-gray-100 text-gray-800 rounded text-[12px] font-mono">$1</code>')
  .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
  .replace(/(^|[^*])\*([^*\n]+)\*/g, '$1<em>$2</em>')
  .replace(/\[([^\]]+)\]\((https?:\/\/[^)]+)\)/g, (_, label, url) => {
    const isFile = FILE_EXTENSIONS.test(url.split('?')[0]);
    if (isFile) {
      return `<a href="${url}" download target="_blank" rel="noopener noreferrer" class="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-50 border border-blue-200 text-blue-700 rounded-md text-[12px] font-medium hover:bg-blue-100">⬇ ${label}</a>`;
    }
    return `<a href="${url}" target="_blank" rel="noopener noreferrer" class="text-blue-600 underline">${label}</a>`;
  })
  .replace(/(^|\s)(https?:\/\/[^\s)<]+)/g, (_, pre, url) => {
    const isFile = FILE_EXTENSIONS.test(url.split('?')[0]);
    if (isFile) {
      const name = url.split('/').pop().split('?')[0] || 'Download';
      return `${pre}<a href="${url}" download target="_blank" rel="noopener noreferrer" class="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-50 border border-blue-200 text-blue-700 rounded-md text-[12px] font-medium hover:bg-blue-100">⬇ ${name}</a>`;
    }
    return `${pre}<a href="${url}" target="_blank" rel="noopener noreferrer" class="text-blue-600 underline">${url}</a>`;
  });

const renderMarkdown = (text) => {
  if (!text) return null;
  const lines = String(text).replace(/\r\n/g, '\n').split('\n');
  const blocks = [];
  let i = 0;
  let key = 0;
  const nextKey = () => `b-${key++}`;

  while (i < lines.length) {
    const line = lines[i];

    if (/^```/.test(line)) {
      const buf = [];
      i += 1;
      while (i < lines.length && !/^```/.test(lines[i])) { buf.push(lines[i]); i += 1; }
      i += 1;
      blocks.push(
        <pre key={nextKey()} className="bg-gray-900 text-gray-100 rounded-lg p-3 overflow-x-auto text-[12px] my-2">
          <code>{buf.join('\n')}</code>
        </pre>
      );
      continue;
    }

    const isPipeLine = (l) => /\|/.test(l) && l.trim().startsWith('|');
    const isSeparator = (l) => /^\s*\|?\s*:?-{2,}:?\s*(\|\s*:?-{2,}:?\s*)+\|?\s*$/.test(l);
    if (isPipeLine(line) && i + 1 < lines.length && isSeparator(lines[i + 1])) {
      const splitRow = (l) => l.trim().replace(/^\|/, '').replace(/\|$/, '').split('|').map((c) => c.trim());
      const header = splitRow(line);
      i += 2;
      const rows = [];
      while (i < lines.length && isPipeLine(lines[i])) { rows.push(splitRow(lines[i])); i += 1; }
      blocks.push(
        <div key={nextKey()} className="my-3 -mx-1 overflow-x-auto">
          <table className="min-w-full text-[13px] border border-gray-200 rounded-md overflow-hidden">
            <thead className="bg-gray-50 text-gray-700">
              <tr>
                {header.map((h, idx) => (
                  <th key={idx} className="text-left font-semibold px-3 py-2 border-b border-gray-200" dangerouslySetInnerHTML={{ __html: renderInline(h) }} />
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, rIdx) => (
                <tr key={rIdx} className={rIdx % 2 ? 'bg-gray-50/50' : 'bg-white'}>
                  {header.map((_, cIdx) => (
                    <td key={cIdx} className="px-3 py-2 align-top border-b border-gray-100" dangerouslySetInnerHTML={{ __html: renderInline(r[cIdx] || '') }} />
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
      continue;
    }

    const heading = /^(#{1,6})\s+(.*)$/.exec(line);
    if (heading) {
      const level = heading[1].length;
      const sizes = ['text-xl', 'text-lg', 'text-base', 'text-base', 'text-sm', 'text-sm'];
      const Tag = `h${Math.min(level + 1, 6)}`;
      blocks.push(
        React.createElement(Tag, {
          key: nextKey(),
          className: `${sizes[level - 1]} font-semibold text-gray-900 mt-3 mb-1`,
          dangerouslySetInnerHTML: { __html: renderInline(heading[2]) }
        })
      );
      i += 1;
      continue;
    }

    if (/^\s*[-*]\s+/.test(line)) {
      const items = [];
      while (i < lines.length && /^\s*[-*]\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^\s*[-*]\s+/, ''));
        i += 1;
      }
      blocks.push(
        <ul key={nextKey()} className="list-disc pl-5 my-2 space-y-1">
          {items.map((it, idx) => (<li key={idx} dangerouslySetInnerHTML={{ __html: renderInline(it) }} />))}
        </ul>
      );
      continue;
    }

    if (/^\s*\d+\.\s+/.test(line)) {
      const items = [];
      while (i < lines.length && /^\s*\d+\.\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^\s*\d+\.\s+/, ''));
        i += 1;
      }
      blocks.push(
        <ol key={nextKey()} className="list-decimal pl-5 my-2 space-y-1">
          {items.map((it, idx) => (<li key={idx} dangerouslySetInnerHTML={{ __html: renderInline(it) }} />))}
        </ol>
      );
      continue;
    }

    if (line.trim() === '') { i += 1; continue; }

    const para = [line];
    i += 1;
    while (
      i < lines.length &&
      lines[i].trim() !== '' &&
      !/^```/.test(lines[i]) &&
      !/^(#{1,6})\s+/.test(lines[i]) &&
      !/^\s*[-*]\s+/.test(lines[i]) &&
      !/^\s*\d+\.\s+/.test(lines[i]) &&
      !(/\|/.test(lines[i]) && lines[i].trim().startsWith('|'))
    ) {
      para.push(lines[i]);
      i += 1;
    }
    blocks.push(
      <p key={nextKey()} className="my-2 leading-relaxed text-gray-800" dangerouslySetInnerHTML={{ __html: renderInline(para.join(' ')) }} />
    );
  }

  return blocks;
};

const SUGGESTIONS = [
  'How many active projects do we have?',
  'List the 5 most recent leads',
  'Show all events scheduled for next week',
  'Give me a workspace summary'
];

// ---------- API helpers ----------

const authHeaders = () => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${localStorage.getItem('token') || ''}`
});

// ---------- Component ----------

const AIChat = () => {
  const apiBase = useMemo(() => `${config.apiUrl}/api/ai`, []);

  // conversations: array of summaries { _id, title, createdAt, updatedAt, messageCount, messages? }
  const [conversations, setConversations] = useState([]);
  const [activeId, setActiveId] = useState(() => {
    try { return localStorage.getItem(ACTIVE_KEY) || null; } catch (_) { return null; }
  });
  const [activeConv, setActiveConv] = useState(null); // full conversation including messages
  const [loadingList, setLoadingList] = useState(true);
  const [loadingActive, setLoadingActive] = useState(false);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [renameDialog, setRenameDialog] = useState({ open: false, id: null, value: '' });
  const [confirmDialog, setConfirmDialog] = useState({ open: false, title: '', message: '', confirmText: 'Confirm', type: 'danger', onConfirm: null });
  const [sidebarOpen, setSidebarOpen] = useState(() => {
    if (typeof window === 'undefined') return true;
    return window.innerWidth >= 768;
  });
  const [query, setQuery] = useState('');
  const scrollerRef = useRef(null);

  const messages = activeConv?.messages || [];

  // Load conversation list on mount
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadingList(true);
      try {
        const res = await fetch(`${apiBase}/conversations`, { headers: authHeaders() });
        const data = await res.json();
        if (!res.ok || !data.success) throw new Error(data.message || 'Failed to load conversations');
        if (cancelled) return;
        let list = data.conversations || [];
        if (list.length === 0) {
          const create = await fetch(`${apiBase}/conversations`, { method: 'POST', headers: authHeaders(), body: '{}' });
          const created = await create.json();
          if (created.success && created.conversation) {
            list = [{
              _id: created.conversation._id,
              title: created.conversation.title,
              createdAt: created.conversation.createdAt,
              updatedAt: created.conversation.updatedAt,
              messageCount: 0
            }];
          }
        }
        setConversations(list);
        // Pick a sensible active: previously-active if still present, else newest
        const stored = localStorage.getItem(ACTIVE_KEY);
        const pick = list.find((c) => c._id === stored) || list[0];
        if (pick) setActiveId(pick._id);
      } catch (err) {
        showToast(err.message || 'Failed to load conversations', 'error');
      } finally {
        if (!cancelled) setLoadingList(false);
      }
    })();
    return () => { cancelled = true; };
  }, [apiBase]);

  // Persist active id
  useEffect(() => {
    if (activeId) { try { localStorage.setItem(ACTIVE_KEY, activeId); } catch (_) {} }
  }, [activeId]);

  // Load full active conversation when activeId changes
  useEffect(() => {
    if (!activeId) { setActiveConv(null); return; }
    let cancelled = false;
    (async () => {
      setLoadingActive(true);
      try {
        const res = await fetch(`${apiBase}/conversations/${activeId}`, { headers: authHeaders() });
        const data = await res.json();
        if (!res.ok || !data.success) throw new Error(data.message || 'Failed to load conversation');
        if (!cancelled) setActiveConv(data.conversation);
      } catch (err) {
        if (!cancelled) showToast(err.message || 'Failed to load conversation', 'error');
      } finally {
        if (!cancelled) setLoadingActive(false);
      }
    })();
    return () => { cancelled = true; };
  }, [activeId, apiBase]);

  // Auto-scroll
  useEffect(() => {
    if (scrollerRef.current) scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
  }, [messages.length, sending]);

  const upsertConversationSummary = useCallback((conv) => {
    setConversations((list) => {
      const summary = {
        _id: conv._id,
        title: conv.title,
        createdAt: conv.createdAt,
        updatedAt: conv.updatedAt,
        messageCount: conv.messages?.length ?? conv.messageCount ?? 0
      };
      const idx = list.findIndex((c) => c._id === conv._id);
      const next = idx >= 0 ? [...list.slice(0, idx), summary, ...list.slice(idx + 1)] : [summary, ...list];
      return next.sort((a, b) => new Date(b.updatedAt || 0) - new Date(a.updatedAt || 0));
    });
  }, []);

  const sendMessage = async (text) => {
    const trimmed = (text || '').trim();
    if (!trimmed || sending || !activeConv) return;
    // Optimistic append
    const optimistic = {
      ...activeConv,
      messages: [...(activeConv.messages || []), { role: 'user', content: trimmed, createdAt: new Date().toISOString() }]
    };
    setActiveConv(optimistic);
    setInput('');
    setSending(true);
    try {
      const res = await fetch(`${apiBase}/chat`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ conversationId: activeConv._id, message: trimmed })
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.message || `Request failed (${res.status})`);
      if (data.conversation) {
        setActiveConv(data.conversation);
        upsertConversationSummary(data.conversation);
      }
    } catch (err) {
      showToast(err.message || 'AI chat failed', 'error');
      setActiveConv((cur) => cur ? ({
        ...cur,
        messages: [...(cur.messages || []), { role: 'assistant', content: `⚠️ ${err.message || 'AI chat failed'}` }]
      }) : cur);
    } finally {
      setSending(false);
    }
  };

  const startNewConversation = async () => {
    try {
      const res = await fetch(`${apiBase}/conversations`, { method: 'POST', headers: authHeaders(), body: '{}' });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.message || 'Failed to create');
      upsertConversationSummary(data.conversation);
      setActiveId(data.conversation._id);
      setInput('');
    } catch (err) {
      showToast(err.message || 'Failed to create conversation', 'error');
    }
  };

  const deleteConversation = (id) => {
    setConfirmDialog({
      open: true,
      title: 'Delete conversation',
      message: 'Are you sure you want to delete this conversation? This cannot be undone.',
      confirmText: 'Delete',
      type: 'danger',
      onConfirm: async () => {
        try {
          const res = await fetch(`${apiBase}/conversations/${id}`, { method: 'DELETE', headers: authHeaders() });
          const data = await res.json();
          if (!res.ok || !data.success) throw new Error(data.message || 'Failed to delete');
          setConversations((list) => {
            const next = list.filter((c) => c._id !== id);
            if (id === activeId) {
              if (next.length > 0) setActiveId(next[0]._id);
              else { setActiveId(null); setActiveConv(null); startNewConversation(); }
            }
            return next;
          });
        } catch (err) {
          showToast(err.message || 'Failed to delete conversation', 'error');
        }
      }
    });
  };

  const renamePrompt = (conv) => {
    if (!conv) return;
    setRenameDialog({ open: true, id: conv._id, value: conv.title || '' });
  };

  const commitRename = async (id, val) => {
    const title = String(val || '').trim() || 'Untitled chat';
    try {
      const res = await fetch(`${apiBase}/conversations/${id}`, {
        method: 'PATCH',
        headers: authHeaders(),
        body: JSON.stringify({ title })
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.message || 'Failed to rename');
      upsertConversationSummary(data.conversation);
      if (activeConv?._id === id) setActiveConv((cur) => cur ? { ...cur, title } : cur);
    } catch (err) {
      showToast(err.message || 'Failed to rename', 'error');
    }
  };

  const clearActive = () => {
    if (!activeConv) return;
    setConfirmDialog({
      open: true,
      title: 'Clear conversation',
      message: 'Clear all messages in this conversation? The conversation itself will be kept.',
      confirmText: 'Clear',
      type: 'warning',
      onConfirm: async () => {
        try {
          const res = await fetch(`${apiBase}/conversations/${activeConv._id}`, {
            method: 'PATCH',
            headers: authHeaders(),
            body: JSON.stringify({ clear: true })
          });
          const data = await res.json();
          if (!res.ok || !data.success) throw new Error(data.message || 'Failed to clear');
          setActiveConv(data.conversation);
          upsertConversationSummary(data.conversation);
        } catch (err) {
          showToast(err.message || 'Failed to clear', 'error');
        }
      }
    });
  };

  const sortedConversations = [...conversations].sort((a, b) => new Date(b.updatedAt || 0) - new Date(a.updatedAt || 0));
  const filteredConversations = query.trim()
    ? sortedConversations.filter((c) => (c.title || '').toLowerCase().includes(query.trim().toLowerCase()))
    : sortedConversations;

  // Group by recency buckets
  const startOfDay = (d) => { const x = new Date(d); x.setHours(0, 0, 0, 0); return x.getTime(); };
  const todayStart = startOfDay(new Date());
  const yesterdayStart = todayStart - 86400000;
  const sevenDaysStart = todayStart - 6 * 86400000;
  const groupOrder = ['Today', 'Yesterday', 'Previous 7 days', 'Older'];
  const grouped = filteredConversations.reduce((acc, c) => {
    const t = new Date(c.updatedAt || c.createdAt).getTime();
    let bucket = 'Older';
    if (t >= todayStart) bucket = 'Today';
    else if (t >= yesterdayStart) bucket = 'Yesterday';
    else if (t >= sevenDaysStart) bucket = 'Previous 7 days';
    (acc[bucket] = acc[bucket] || []).push(c);
    return acc;
  }, {});

  const active = activeConv || conversations.find((c) => c._id === activeId);

  const selectConversation = (id) => {
    setActiveId(id);
    if (typeof window !== 'undefined' && window.innerWidth < 768) setSidebarOpen(false);
  };

  return (
    <DashboardLayout>
      <div className="relative flex h-[calc(100vh-64px)] bg-gray-50 overflow-hidden">
        {/* Mobile backdrop */}
        {sidebarOpen && (
          <div
            className="md:hidden fixed inset-0 bg-black/40 z-30"
            onClick={() => setSidebarOpen(false)}
            aria-hidden="true"
          />
        )}
        {/* Conversations sidebar */}
        <aside
          className={`${
            sidebarOpen ? 'translate-x-0 w-[85vw] sm:w-80 md:w-80' : '-translate-x-full w-0 md:w-0'
          } fixed md:relative inset-y-0 left-0 z-40 md:z-auto md:translate-x-0 ${sidebarOpen ? '' : 'md:!w-0'} transition-all duration-200 overflow-hidden bg-gradient-to-b from-slate-50 to-white border-r border-gray-200 flex flex-col h-full shadow-xl md:shadow-none`}
        >
          {/* Header */}
          <div className="px-4 pt-4 pb-3 border-b border-gray-200">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-[13px] font-semibold uppercase tracking-wide text-gray-500">Conversations</h2>
              <span className="text-[11px] text-gray-400">{conversations.length}</span>
            </div>
            <button
              type="button"
              onClick={startNewConversation}
              className="w-full inline-flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 shadow-sm transition"
            >
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none"><path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>
              New chat
            </button>
            <div className="relative mt-3">
              <svg className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" viewBox="0 0 24 24" fill="none">
                <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="1.5"/>
                <path d="M20 20l-3.5-3.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
              </svg>
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search"
                className="w-full pl-9 pr-8 py-2 text-sm bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              {query && (
                <button
                  type="button"
                  onClick={() => setQuery('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-gray-100 text-gray-400 hover:text-gray-600"
                  title="Clear search"
                >
                  <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none"><path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>
                </button>
              )}
            </div>
          </div>

          {/* List */}
          <div className="flex-1 overflow-y-auto py-2">
            {loadingList ? (
              <div className="px-4 py-6 text-sm text-gray-400">Loading…</div>
            ) : filteredConversations.length === 0 ? (
              <div className="px-4 py-10 text-center">
                <div className="w-12 h-12 mx-auto rounded-full bg-gray-100 flex items-center justify-center mb-2">
                  <svg className="w-6 h-6 text-gray-400" viewBox="0 0 24 24" fill="none">
                    <path d="M21 15a4 4 0 01-4 4H8l-5 3V7a4 4 0 014-4h10a4 4 0 014 4v8z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
                  </svg>
                </div>
                <p className="text-sm text-gray-500">{query ? 'No matches' : 'No conversations yet'}</p>
                {!query && <p className="text-[11px] text-gray-400 mt-1">Start a new chat above.</p>}
              </div>
            ) : groupOrder.filter((g) => grouped[g]?.length).map((bucket) => (
              <div key={bucket} className="mb-3">
                <div className="px-4 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-wider text-gray-400">{bucket}</div>
                {grouped[bucket].map((c) => {
                  const isActive = c._id === activeId;
                  return (
                    <div
                      key={c._id}
                      className={`group relative mx-2 my-0.5 rounded-lg pl-3 pr-2 py-2 cursor-pointer flex items-center gap-2 transition ${
                        isActive ? 'bg-blue-50 border border-blue-200 shadow-sm' : 'hover:bg-white border border-transparent hover:border-gray-200 hover:shadow-sm'
                      }`}
                      onClick={() => selectConversation(c._id)}
                      onDoubleClick={() => renamePrompt(c)}
                    >
                      {isActive && (
                        <span className="absolute left-0 top-1/2 -translate-y-1/2 h-6 w-1 rounded-r bg-blue-600" />
                      )}
                      <div className="flex-1 min-w-0">
                        <div className={`text-sm truncate ${isActive ? 'text-blue-900 font-semibold' : 'text-gray-800 font-medium'}`}>{c.title}</div>
                        <div className="text-[11px] text-gray-400 flex items-center gap-1">
                          <span>{new Date(c.updatedAt || c.createdAt).toLocaleString([], { hour: '2-digit', minute: '2-digit' })}</span>
                          {c.messageCount > 0 && (
                            <>
                              <span className="text-gray-300">·</span>
                              <span>{c.messageCount} msg</span>
                            </>
                          )}
                        </div>
                      </div>
                      <div className="opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-0.5">
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); renamePrompt(c); }}
                          className="p-1.5 rounded hover:bg-gray-100 text-gray-500"
                          title="Rename"
                        >
                          <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none"><path d="M4 20h4l10-10-4-4L4 16v4z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/></svg>
                        </button>
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); deleteConversation(c._id); }}
                          className="p-1.5 rounded hover:bg-red-50 text-gray-500 hover:text-red-600"
                          title="Delete"
                        >
                          <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none"><path d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2m1 0v14a2 2 0 01-2 2H9a2 2 0 01-2-2V6h10z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/></svg>
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>

          {/* Footer */}
          <div className="px-4 py-3 border-t border-gray-200 bg-white/50">
            <div className="flex items-center gap-2 text-[11px] text-gray-500">
              <svg className="w-3.5 h-3.5 text-emerald-500" viewBox="0 0 24 24" fill="none"><path d="M5 13l4 4L19 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
              <span>Saved to your account</span>
            </div>
          </div>
        </aside>

        {/* Main chat column */}
        <div className="flex-1 flex flex-col min-w-0">
          {/* Header */}
          <div className="flex items-center gap-2 sm:gap-3 px-3 sm:px-4 py-3 bg-white border-b border-gray-200">
            <button
              type="button"
              onClick={() => setSidebarOpen((v) => !v)}
              className="p-2 rounded-md hover:bg-gray-100 text-gray-500 shrink-0"
              title={sidebarOpen ? 'Hide conversations' : 'Show conversations'}
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none"><path d="M4 6h16M4 12h16M4 18h16" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/></svg>
            </button>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <h1
                  className="text-base font-semibold text-gray-900 truncate cursor-text"
                  title="Double-click to rename"
                  onDoubleClick={() => active && renamePrompt(active)}
                >
                  {active?.title || 'Topsail roofing Chat'}
                </h1>
                {active && (
                  <button
                    type="button"
                    onClick={() => renamePrompt(active)}
                    className="p-1 rounded hover:bg-gray-100 text-gray-400 hover:text-gray-600"
                    title="Rename conversation"
                  >
                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none"><path d="M4 20h4l10-10-4-4L4 16v4z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/></svg>
                  </button>
                )}
              </div>
              <p className="text-xs text-gray-500 truncate hidden sm:block">Ask about leads, projects, calendars, crews, clients — or have me create things for you.</p>
            </div>
            <button
              type="button"
              onClick={clearActive}
              className="text-sm px-2 sm:px-3 py-1.5 rounded-md border border-gray-300 hover:bg-gray-100 shrink-0"
            >
              Clear
            </button>
          </div>

          {/* Messages */}
          <div ref={scrollerRef} className="flex-1 overflow-y-auto px-4 sm:px-6 py-6">
            {loadingActive && messages.length === 0 ? (
              <div className="max-w-3xl mx-auto text-center text-sm text-gray-400 py-10">Loading conversation…</div>
            ) : messages.length === 0 ? (
              <div className="max-w-3xl mx-auto">
                <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
                  <h2 className="text-lg font-semibold mb-2 text-gray-900">Welcome to Topsail roofing Chat</h2>
                  <p className="text-gray-600 mb-4">I can search the CRM, summarize records, and (if you're an admin) create or update them.</p>
                  <div className="flex flex-wrap gap-2">
                    {SUGGESTIONS.map((s) => (
                      <button
                        key={s}
                        type="button"
                        onClick={() => sendMessage(s)}
                        className="text-sm px-3 py-1.5 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-200"
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <div className="max-w-3xl mx-auto space-y-4">
                {messages.map((m, idx) => (
                  <div key={idx} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                    {m.role === 'assistant' && (
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 text-white flex items-center justify-center text-xs font-semibold mr-2 shrink-0">AI</div>
                    )}
                    <div className={`max-w-[90%] sm:max-w-[85%] rounded-2xl px-3 sm:px-4 py-2.5 sm:py-3 shadow-sm border text-sm ${
                      m.role === 'user'
                        ? 'bg-blue-600 text-white border-blue-600 rounded-br-sm'
                        : 'bg-white text-gray-900 border-gray-200 rounded-bl-sm'
                    }`}>
                      {m.role === 'assistant' && m.toolEvents?.length > 0 && (
                        <div className="mb-2 flex flex-wrap gap-1">
                          {m.toolEvents.map((ev, i) => (
                            <span
                              key={i}
                              className={`text-[10px] px-2 py-0.5 rounded-full border font-mono ${
                                ev.result?.error
                                  ? 'bg-red-50 text-red-700 border-red-200'
                                  : 'bg-gray-50 text-gray-600 border-gray-200'
                              }`}
                              title={JSON.stringify(ev.result).slice(0, 600)}
                            >
                              {formatToolEvent(ev)}
                            </span>
                          ))}
                        </div>
                      )}
                      {m.role === 'user' ? (
                        <div className="whitespace-pre-wrap">{m.content}</div>
                      ) : (
                        <div className="prose-sm">{renderMarkdown(m.content)}</div>
                      )}
                    </div>
                  </div>
                ))}
                {sending && (
                  <div className="flex justify-start items-end">
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 text-white flex items-center justify-center text-xs font-semibold mr-2 shrink-0">AI</div>
                    <div className="bg-white border border-gray-200 rounded-2xl rounded-bl-sm px-4 py-3 text-sm text-gray-500 shadow-sm">
                      <span className="inline-flex items-center gap-2">
                        <span className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" />
                        <span className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '0.15s' }} />
                        <span className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '0.3s' }} />
                        Thinking…
                      </span>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Composer */}
          <div className="border-t border-gray-200 bg-white px-3 sm:px-6 py-3 sm:py-4 pb-[env(safe-area-inset-bottom)]">
            <form
              className="max-w-3xl mx-auto flex items-end gap-2"
              onSubmit={(e) => { e.preventDefault(); sendMessage(input); }}
            >
              <textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    sendMessage(input);
                  }
                }}
                placeholder="Ask anything about the CRM…"
                rows={2}
                className="flex-1 resize-none rounded-xl border border-gray-300 px-3 sm:px-4 py-2.5 sm:py-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                disabled={sending || !activeConv}
              />
              <button
                type="submit"
                disabled={sending || !input.trim() || !activeConv}
                className="px-4 sm:px-5 py-2.5 sm:py-3 rounded-xl bg-blue-600 text-white font-medium text-sm disabled:opacity-50 hover:bg-blue-700 shrink-0"
              >
                {sending ? '…' : 'Send'}
              </button>
            </form>
            <div className="max-w-3xl mx-auto text-[11px] text-gray-400 mt-2 text-center hidden sm:block">
              Enter to send · Shift+Enter for newline · Conversations saved to your account
            </div>
          </div>
        </div>
      </div>

      <PromptDialog
        isOpen={renameDialog.open}
        onClose={() => setRenameDialog({ open: false, id: null, value: '' })}
        onSubmit={(val) => commitRename(renameDialog.id, val)}
        title="Rename conversation"
        defaultValue={renameDialog.value}
        placeholder="Conversation name"
        confirmText="Save"
      />

      <ConfirmDialog
        isOpen={confirmDialog.open}
        onClose={() => setConfirmDialog((c) => ({ ...c, open: false }))}
        onConfirm={() => { confirmDialog.onConfirm && confirmDialog.onConfirm(); }}
        title={confirmDialog.title}
        message={confirmDialog.message}
        confirmText={confirmDialog.confirmText}
        type={confirmDialog.type}
      />
    </DashboardLayout>
  );
};

export default AIChat;
